/**
 * # Price Promotion Key (PPK) Context Management
 *
 * This composable manages a reactive cache that tracks promotional pricing contexts across product families.
 * It resolves and stores relationships between products, their variants, order numbers, and price promotion keys.
 *
 * ## What is a Price Promotion Key (PPK)?
 *
 * A PPK is an identifier used in e-commerce systems to determine which promotional pricing rules apply
 * to specific products/variants. Products can have multiple pricing scenarios (regular, sale, member-only, etc.)
 * and the PPK determines which pricing context is active.
 *
 * ## Why This Context Manager Exists
 *
 * **Problem**: Products and their variants can have complex pricing relationships:
 * - A single product may have multiple order numbers with different PPKs
 * - Sibling products (same style, different colors) share promotional contexts
 * - Variants (sizes) may have their own specific order numbers and PPKs
 * - Components need to react when pricing context changes or gets reset
 *
 * **Solution**: This cache provides:
 * - Centralized storage of PPK contexts per product family (masterKey)
 * - Reactive updates when contexts change or are flushed
 * - Efficient lookups for product/variant-specific pricing information
 * - Automatic fallback to default pricing when specific PPK is unavailable
 *
 * ## Key Concepts
 *
 * - **Master Context**: All products with the same `masterKey` (product family) share one context
 * - **Article Placement**: Maps order numbers to PPKs for pricing resolution
 * - **Product Entry**: Stores PPK info for the main product
 * - **Variant Entries**: Stores PPK info for each size/color variant
 * - **PPK Resolution Hierarchy**: When resolving pricing for products/variants:
 *   1. First attempts to use the master PPK from the context
 *   2. If no article placement exists for the master PPK, falls back to DEFAULT_PRICE_PROMOTION_KEY ('99990000')
 *   3. This ensures pricing is always available even when promotional contexts don't match
 *
 * ## Usage Patterns
 *
 * ```typescript
 * const { init, useMasterContext, flush, setVariantEntries } = usePpkContext()
 *
 * // Initialize context for a product family
 * init(product, highlightedOrderNumber)
 *
 * // Get reactive reference to context (updates when flushed/re-initialized)
 * const { context: ppkContext, getVariantEntry } = useMasterContext(product)
 *
 * // Use in computed properties
 * const pricePromotionKey = computed(() => ppkContext.value?.ppk)
 *
 * // Populate variant-specific data
 * setVariantEntries(variants, product)
 *
 * // Reset context (e.g., on navigation)
 * flush(product)
 * ```
 *
 * ## Cache Structure
 *
 * ```
 * cache[masterKey] = {
 *   ppk: "promotion_key_123",
 *   products: {
 *     [productId]: {
 *       entry: { orderNumber: "1234-567", ppk: "promotion_key_123" },
 *       variants: {
 *         [variantId]: { orderNumber: "1234-567", ppk: "promotion_key_123" }
 *       }
 *     }
 *   }
 * }
 * ```
 *
 * @see {@link DEFAULT_PRICE_PROMOTION_KEY} - Default fallback PPK ('99990000')
 * @see {@link getArticlePlacementByPpk} - Resolves order number from PPK
 * @see {@link getVariantArticlePlacementByPpk} - Resolves variant-specific mappings
 */

import type { Product, Variant } from '@scayle/storefront-nuxt'
import { reactive } from 'vue'
import {
  getArticlePlacementByPpk,
  getVariantArticlePlacementByPpk,
  getPricePromotionKey,
  type ArticlePlacementEntry,
} from '~/utils/product'
import { DEFAULT_PRICE_PROMOTION_KEY } from '../constants/withParameters'

export interface PpkCache {
  [masterKey: string]: MasterContext
}

export interface MasterContext {
  ppk?: string
  products: Record<string, ProductEntry>
}

export interface ProductEntry {
  entry: ArticlePlacementEntry
  variants: Record<string, ArticlePlacementEntry>
}

const cache = reactive<PpkCache>({})

const EMPTY_ENTRY: ArticlePlacementEntry = {
  orderNumber: undefined,
  ppk: undefined,
}

export default function usePpkContext() {
  const init = (product: Product, orderNumber?: string, cb = () => {}) => {
    if (!product.masterKey) return

    const ppk = orderNumber
      ? getPricePromotionKey(product, orderNumber)
      : undefined
    const allSiblings = [product, ...(product.siblings || [])]
    const products = getProductEntries(allSiblings, ppk)

    cache[product.masterKey] = {
      ppk,
      products,
    }

    cb()

    return cache[product.masterKey]
  }

  const getMasterContext = (product: Product) => {
    return product.masterKey ? cache[product.masterKey] : undefined
  }

  const getProductEntries = (products: Product[], nominalPpk?: string) => {
    return products.reduce(
      (acc, product) => {
        const entry =
          getArticlePlacementByPpk(product, nominalPpk) ??
          getArticlePlacementByPpk(product, DEFAULT_PRICE_PROMOTION_KEY) ??
          EMPTY_ENTRY

        acc[product.id] = { entry, variants: {} }
        return acc
      },
      {} as Record<string, ProductEntry>,
    )
  }

  const setVariantEntries = (variants: Variant[], product: Product) => {
    const masterContext = getMasterContext(product)
    if (!masterContext) return

    const entries = variants.reduce(
      (acc, variant) => {
        acc[variant.id] =
          getVariantArticlePlacementByPpk(
            variant,
            product,
            masterContext.ppk,
          ) ??
          getVariantArticlePlacementByPpk(
            variant,
            product,
            DEFAULT_PRICE_PROMOTION_KEY,
          ) ??
          EMPTY_ENTRY

        return acc
      },
      {} as Record<string, ArticlePlacementEntry>,
    )

    masterContext.products[product.id]!.variants = entries
  }

  const getProductEntry = (product: Product) => {
    const cache = getMasterContext(product)
    return cache ? cache.products[product.id]?.entry : EMPTY_ENTRY
  }

  const getVariantEntry = (variant: Variant, product: Product) => {
    const cache = getMasterContext(product)
    return cache
      ? cache.products[product.id]?.variants[variant.id]
      : EMPTY_ENTRY
  }

  const flush = (product: Product) => {
    if (!product.masterKey) return
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete cache[product.masterKey]
  }

  const isContextSet = (product: Product) => {
    return product.masterKey ? !!cache[product.masterKey] : false
  }

  const useContextCheck = (product: Product) => {
    return computed(() => {
      return product.masterKey ? !!cache[product.masterKey] : true
    })
  }

  return {
    init,
    flush,
    setVariantEntries,
    getProductEntry,
    getVariantEntry,
    useContextCheck,
    cache,
    isContextSet,
  }
}
